import { useEffect, useRef, useState } from "react";
import type { BillboardScene } from "./billboardScene";
import type { HeroLocale } from "./promoScreen";
import { ICON_PATH } from "./logoPaths";

const QR_URL = "https://on45d.studio/contact";

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function saveData() {
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } };
  return Boolean(nav.connection?.saveData);
}

/**
 * The hero's 3D billboard street. three.js and the scene load as a separate chunk once the page is
 * idle, so the hero text and CTA never wait for them. Until then (or with no WebGL, or Save-Data on)
 * the sheet shows a static line drawing of the 45D icon.
 */
export function HeroScene({
  host,
  terminal,
  locale,
}: {
  host: React.RefObject<HTMLElement>;
  terminal: React.RefObject<HTMLDivElement>;
  locale: HeroLocale;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<BillboardScene | null>(null);
  const localeRef = useRef(locale);
  const [ready, setReady] = useState(false);
  localeRef.current = locale;

  useEffect(() => {
    if (saveData()) return;
    let cancelled = false;
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const onMotion = () => sceneRef.current?.setReducedMotion(prefersReducedMotion());
    // The scene paints text into textures once (signs, decals), so the fonts must be ready first.
    const fontsReady = () => {
      const fonts = document.fonts;
      if (!fonts?.load) return Promise.resolve();
      const wanted = ['700 52px "Red Hat Mono"', '500 26px "Red Hat Mono"', '900 96px "Red Hat Display"'];
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 2500));
      return Promise.race([Promise.all(wanted.map((f) => fonts.load(f))), timeout]).catch(() => undefined);
    };
    const start = () => {
      Promise.all([import("./billboardScene"), fontsReady()])
        .then(([{ createBillboardScene }]) => {
          const canvas = canvasRef.current, hostEl = host.current, termEl = terminal.current;
          if (cancelled || !canvas || !hostEl || !termEl) return;
          const scene = createBillboardScene({
            canvas,
            host: hostEl,
            terminal: termEl,
            locale: localeRef.current,
            reducedMotion: prefersReducedMotion(),
            qrUrl: QR_URL,
          });
          if (!scene) return;
          sceneRef.current = scene;
          media?.addEventListener?.("change", onMotion);
          setReady(true);
        })
        .catch(() => {
          /* keep the static drawing */
        });
    };
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const handle = idle ? idle(start, { timeout: 1500 }) : window.setTimeout(start, 300);
    return () => {
      cancelled = true;
      if (!idle) window.clearTimeout(handle);
      media?.removeEventListener?.("change", onMotion);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    sceneRef.current?.setLocale(locale);
  }, [locale]);

  return (
    <>
      <div className={`hero-bp-fallback ${ready ? "is-hidden" : ""}`} aria-hidden="true">
        <svg viewBox="0 0 419.14 345.56">
          <rect x="345.56" width="73.58" height="345.56" />
          <path d={ICON_PATH} />
        </svg>
      </div>
      <canvas
        ref={canvasRef}
        className={`hero-bp-canvas ${ready ? "is-ready" : ""}`}
        role="img"
        aria-label={
          locale === "es"
            ? "Calle dibujada como plano con una valla de ON45D que gira. Toca la valla para encenderla."
            : "Blueprint street scene with a rotating ON45D billboard. Tap the billboard to light it up."
        }
      />
    </>
  );
}
